import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UniqueIdentifier } from "@dnd-kit/core";
import { DragDropContext } from "@/components/Dashboard/components/DnD/DragDropContext";
import { ProfiledSortableContainer } from "@/components/Dashboard/components/DnD/SortableContainer";
import { SortableItem } from "@/components/Dashboard/components/DnD/SortableItem";
import { useLayouts } from "@/hooks/useLayouts";
import { useStore } from "@/stores/useStore";
import { useParams } from "react-router-dom";
import {
  DEFAULT_DASHBOARD_SCALING,
  SHOW_EMPTY_CONTAINER_PLACEHOLDER,
} from "@/lib/const";
import { httpsCallables } from "@/repositories/httpsCallables";
import { useCardFiltering } from "@/hooks/useCardFiltering";
import { useRoles } from "@/hooks/useRoles";
import { useDebounceCallback } from "@/hooks/useDebounceCallback";
import { MemoizedCard } from "@/components/Dashboard/components/DnD/SortableCard";
import { useInboxPanel } from "@/hooks/useInboxPanel";
import { InboxPanel } from "@/components/Common/InboxPanel";
import { MoveToDialog } from "@/components/DashboardList/components/MoveToDialog";
import { useMoveToDialog } from "@/components/DashboardList/hooks/useMoveToDialog";
import { useLabelChoiceDialog } from "@/components/Common/LabelChoiceDialog";
import { resolveBidirectionalLabelChoice } from "@/lib/resolveBidirectionalLabelChoice";
import { BOARD_HEIGHT_CSS, BOARD_WRAPPER_PADDING_PX } from "@/lib/layout";
import { useTechnicianChoiceDialog } from "@/components/Common/TechnicianChoiceDialog";
import { resolveTechnicianChoice } from "@/lib/resolveTechnicianChoice";
import { isTechnicianWritebackEnabledForShop } from "@/lib/technicianWriteback";
import { toast } from "@/hooks/useToast";

type DashboardProps = {
  selectedLayoutId: string;
};

// Relocates every pinned card into its pinned container within `next` (a
// freshly-derived containers snapshot), leaving everything else untouched.
// Used by the shop-doc resync effect below so an unrelated Firestore
// snapshot landing while a card move is in flight doesn't visibly snap the
// card back to its stale server position — see WEBV3-307.
export type PinnedCard = { containerId: UniqueIdentifier; index: number };

export function applyPinnedCards(
  next: Record<UniqueIdentifier, UniqueIdentifier[]>,
  pinned: Map<UniqueIdentifier, PinnedCard>
): Record<UniqueIdentifier, UniqueIdentifier[]> {
  if (pinned.size === 0) return next;

  const result: Record<UniqueIdentifier, UniqueIdentifier[]> = {};
  for (const [containerId, cardIds] of Object.entries(next)) {
    result[containerId] = [...cardIds];
  }

  for (const [cardId, { containerId: pinnedContainerId, index }] of pinned) {
    if (!(pinnedContainerId in result)) continue;

    for (const containerId of Object.keys(result)) {
      if (containerId === pinnedContainerId) continue;
      result[containerId] = result[containerId].filter((id) => id !== cardId);
    }

    if (!result[pinnedContainerId].includes(cardId)) {
      const insertAt = Math.min(index, result[pinnedContainerId].length);
      result[pinnedContainerId] = [
        ...result[pinnedContainerId].slice(0, insertAt),
        cardId,
        ...result[pinnedContainerId].slice(insertAt),
      ];
    }
  }

  return result;
}

// Deletes a card's pin (from both `pinnedCards` and `moveTokens`) only if
// `token` still matches the current owner recorded for that card id —
// guards against a second, still-in-flight move for the same card having
// its pin torn down by an earlier move's `finally` once that earlier move
// settles. A stale token (the id's current owner is a different, newer
// token — or the id isn't pinned at all) is a no-op. See WEBV3-307.
export function clearPinIfOwner(
  pinnedCards: Map<UniqueIdentifier, PinnedCard>,
  moveTokens: Map<UniqueIdentifier, number>,
  id: UniqueIdentifier,
  token: number
): void {
  if (moveTokens.get(id) !== token) return;
  moveTokens.delete(id);
  pinnedCards.delete(id);
}

export const Dashboard = ({ selectedLayoutId }: DashboardProps) => {
  const { shopId } = useParams();
  const { filteredCards } = useCardFiltering();
  const { isViewOnly, isAdmin, isOffice, isSuperAdmin } = useRoles();
  const { shop, employees } = useStore();
  const shopContainers = useMemo(() => shop?.containers ?? [], [shop]);
  const { layouts } = useLayouts();
  const { isOpen: isPanelOpen, isTransitioning, panelWidth } = useInboxPanel();

  // MoveToDialog functionality
  const {
    isOpen: isMoveDialogOpen,
    selectedCard,
    openDialog: openMoveDialog,
    closeDialog: closeMoveDialog,
    handleMoveSuccess,
  } = useMoveToDialog();

  const { chooseLabel, Dialog: LabelChoicePopup } = useLabelChoiceDialog();
  const { chooseTechnician, Dialog: TechnicianChoicePopup } =
    useTechnicianChoiceDialog();
  const isTechnicianWritebackEnabled = shop
    ? isTechnicianWritebackEnabledForShop(shop)
    : false;
  // WEBV3-293: only admin/office (or superAdmin) may assign a technician —
  // when false, the technician picker is suppressed exactly like the shop's
  // write-back setting being off (resolveTechnicianChoice's existing
  // isTechnicianWritebackEnabled gate). The backend independently enforces
  // this too (moveCard.ts's canAssignTechnician clamp) — this is UX, not
  // the security boundary.
  const canAssignTechnician = isAdmin || isOffice || isSuperAdmin;

  const visibleCardIds = useMemo(
    () => filteredCards.map((c) => c.id),
    [filteredCards]
  );

  const selectedLayout = useMemo(() => {
    const layout =
      layouts.find((l) => Object.keys(l).includes(selectedLayoutId)) ??
      layouts[0];
    if (!layout) return undefined;
    return layout[selectedLayoutId as keyof typeof layout];
  }, [selectedLayoutId, layouts]);
  const [containers, setContainers] = useState<
    Record<UniqueIdentifier, UniqueIdentifier[]>
  >(Object.fromEntries(shopContainers.map((c) => [c.id, c.tags])));

  // Cards whose move is currently in flight, mapped to the container (and
  // drop index) they must visually stay at until the move settles.
  // `handleCardDragEnd` sets a card's pin the moment its move starts (before
  // either dialog await) and clears it in a `finally` once the move
  // resolves, fails, or is cancelled. The resync effect below consults this
  // map so an unrelated shop-doc snapshot landing mid-move doesn't revert
  // the optimistic move — see WEBV3-307.
  const pinnedCardsRef = useRef<Map<UniqueIdentifier, PinnedCard>>(new Map());

  // Per-card move ownership token, guarding pin clears against a second drag
  // of the same card starting (and finishing) before an earlier, still
  // in-flight move for that card settles — without this, the earlier move's
  // `finally` would delete the newer move's pin out from under it. Only the
  // call that still owns the current token for a card id may clear its pin
  // (see `clearPinIfOwner` above).
  const nextMoveTokenRef = useRef(0);
  const moveTokensRef = useRef<Map<UniqueIdentifier, number>>(new Map());
  const clearPin = useCallback((id: UniqueIdentifier, token: number) => {
    clearPinIfOwner(pinnedCardsRef.current, moveTokensRef.current, id, token);
  }, []);

  useEffect(() => {
    setContainers(
      applyPinnedCards(
        Object.fromEntries(shopContainers.map((c) => [c.id, c.tags])),
        pinnedCardsRef.current
      )
    );
  }, [shopContainers]);

  // `containers` and `shopContainers` are read via refs (not state) inside
  // `handleCardDragEnd`'s post-await paths so those reads see live state,
  // not the snapshot captured when this drag started — the label-choice
  // popup can stay open long enough for other updates (another drag, a
  // live shop sync) to land in the meantime. Same idiom as `containersRef`
  // in `DragDropContext.tsx`.
  const containersRef = useRef(containers);
  containersRef.current = containers;
  const shopContainersRef = useRef(shopContainers);
  shopContainersRef.current = shopContainers;

  const renderDragOverlay = (id: UniqueIdentifier) => {
    if (shop) {
      return <MemoizedCard cardId={id} isDragging={true} />;
    }

    return null;
  };

  const handleCardDragEnd = useCallback(
    async (id: UniqueIdentifier, containerId: UniqueIdentifier) => {
      const toIndex = containers[containerId].findIndex(
        (itemId) => itemId === id
      );

      if (toIndex === -1 || !shopId) return;

      // Pinned for the duration of this move (cleared in `finally` below)
      // so the resync effect above doesn't revert the optimistic move if
      // an unrelated shop-doc snapshot lands while we're still awaiting a
      // dialog decision or the `moveCard` call itself — see WEBV3-307.
      const moveToken = ++nextMoveTokenRef.current;
      moveTokensRef.current.set(id, moveToken);
      pinnedCardsRef.current.set(id, { containerId, index: toIndex });

      try {
        const card = filteredCards.find((c) => c.id === id);

        if (card && containerId !== card.currentContainer) {
          const toContainer = shopContainers.find((c) => c.id === containerId);
          const result = await resolveBidirectionalLabelChoice(
            toContainer,
            shop?.labelCatalog,
            card,
            chooseLabel
          );

          if (!result.proceed) {
            // handleDragOver already moved this card into `containerId` in
            // local state before drop — the user cancelled, so re-sync all
            // of `containers` from the latest known `shopContainers` (the
            // same derivation the mount-time useEffect above does), rather
            // than surgically moving just this one card back. A card that
            // was never actually moved server-side (moveCard was never
            // called) is still at its original position in `shopContainers`,
            // so this both undoes the optimistic move AND restores its
            // original index — a targeted revert via `moveItemBetweenContainers`
            // could only put it back at the end of its original container.
            // Read via `shopContainersRef`, not the `shopContainers` closed
            // over at drag-start, for the same live-state reason `containersRef`
            // exists. Clear this card's own pin first — otherwise it would
            // be re-applied on top of this revert and stay stuck in the
            // (cancelled) target container — then route through
            // `applyPinnedCards` so any *other* card's still-in-flight move
            // isn't itself reverted by this resync — see WEBV3-307.
            clearPin(id, moveToken);
            setContainers(
              applyPinnedCards(
                Object.fromEntries(
                  shopContainersRef.current.map((c) => [c.id, c.tags])
                ),
                pinnedCardsRef.current
              )
            );
            return;
          }

          const technicianResult = await resolveTechnicianChoice(
            toContainer,
            employees,
            card,
            chooseTechnician,
            isTechnicianWritebackEnabled && canAssignTechnician
          );

          if (!technicianResult.proceed) {
            clearPin(id, moveToken);
            setContainers(
              applyPinnedCards(
                Object.fromEntries(
                  shopContainersRef.current.map((c) => [c.id, c.tags])
                ),
                pinnedCardsRef.current
              )
            );
            return;
          }

          // Recompute against live state — `toIndex` was captured before the
          // popup's await, and the popup can stay open long enough for the
          // container's contents to change in the meantime.
          const liveIndex = containersRef.current[containerId]?.findIndex(
            (itemId) => itemId === id
          );
          const resolvedToIndex =
            liveIndex !== undefined && liveIndex !== -1 ? liveIndex : toIndex;

          const moveResult = await httpsCallables.moveCard({
            cardId: id as string,
            shopId,
            toContainerId: containerId as string,
            toIndex: resolvedToIndex,
            chosenBidirectionalLabel: result.chosenBidirectionalLabel,
            chosenTechnicianId: technicianResult.chosenTechnicianId,
          });

          if (moveResult.data?.technicianSyncFailed) {
            toast({
              variant: "destructive",
              title: "Technician sync failed",
              description:
                "The card moved, but syncing the technician to Tekmetric failed.",
            });
          }

          return;
        }

        // `await` here is required, not redundant: without it, `finally`
        // below clears this move's pin synchronously as `return` executes,
        // before the returned promise settles — reopening the exact race
        // this pin exists to close for this branch. See WEBV3-307.
        return await httpsCallables.moveCard({
          cardId: id as string,
          shopId,
          toContainerId: containerId as string,
          toIndex,
        });
      } finally {
        clearPin(id, moveToken);
      }
    },
    [
      containers,
      shopId,
      shopContainers,
      filteredCards,
      shop?.labelCatalog,
      chooseLabel,
      employees,
      chooseTechnician,
      isTechnicianWritebackEnabled,
      canAssignTechnician,
      clearPin,
    ]
  );

  const debouncedHandleCardDragEnd = useDebounceCallback(
    handleCardDragEnd,
    100
  );

  const layoutGridRows = useMemo(() => {
    const rows = selectedLayout?.map((c) => c.y + c.h);
    return rows ? Math.max(...rows) : 0;
  }, [selectedLayout]);

  const layoutGridColumns = useMemo(() => {
    const columns = selectedLayout?.map((c) => c.x + c.w);
    return columns ? Math.max(...columns) : 0;
  }, [selectedLayout]);

  const gridScaling =
    (shop?.shopConfig?.dashboardScaling ?? DEFAULT_DASHBOARD_SCALING) ===
    "scaleToFit";

  // Pre-compute styles to avoid recalculation
  const containerStyles = useMemo(
    () => ({
      gridTemplateRows: `repeat(${gridScaling ? layoutGridRows : 12},1fr)`,
      gridTemplateColumns: `repeat(${gridScaling ? layoutGridColumns : 96},1fr)`,
    }),
    [gridScaling, layoutGridRows, layoutGridColumns]
  );

  // Use CSS custom properties for dynamic values
  const cssProperties = useMemo(
    () => ({
      "--grid-rows": gridScaling ? layoutGridRows : 12,
      "--grid-columns": gridScaling ? layoutGridColumns : 96,
    }),
    [gridScaling, layoutGridRows, layoutGridColumns]
  );

  // Dynamic width calculation for panel integration
  const dashboardStyles = useMemo(() => {
    const padding = 0; // Total horizontal padding (8px each side)
    const baseWidth = `calc(100vw - ${padding}px)`; // Default width with some padding
    // On mobile (<768px), panel overlays dashboard, so keep full width
    // On tablet/desktop (>=768px), adjust width when panel is open
    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
    const adjustedWidth =
      isPanelOpen && !isMobile
        ? `calc(100vw - ${panelWidth}px - ${padding}px)`
        : baseWidth;

    return {
      width: adjustedWidth,
      transition: "width 300ms ease-in-out",
      willChange: isTransitioning ? "width" : "auto",
    };
  }, [isPanelOpen, panelWidth, isTransitioning]);

  if (!shop) return null;

  return (
    <>
      <div
        className="relative"
        style={{
          // Applied rather than `p-2` so the board's height reservation accounts
          // for the inset it actually gets (WEBV3-256).
          padding: BOARD_WRAPPER_PADDING_PX,
          contain: "layout style paint", // Isolate this component
          ...dashboardStyles,
        }}
      >
        <DragDropContext
          containers={containers}
          onContainersUpdate={setContainers}
          className={"grid gap-1 overflow-auto"}
          style={{
            height: BOARD_HEIGHT_CSS,
            ...containerStyles,
            ...cssProperties,
            willChange: "contents",
          }}
          renderDragOverlay={renderDragOverlay}
          onDragEnd={debouncedHandleCardDragEnd}
        >
          {Object.keys(containers).map((containerId, index) => {
            const gridParams = selectedLayout?.find(
              (paramObj) => paramObj.i === containerId
            );
            const container = shopContainers.find((c) => c.id === containerId);

            if (gridParams && container) {
              const startingRow = gridParams.y + 1;
              const startingColumn = gridParams.x + 1;
              const endingRow = startingRow + gridParams.h;
              const endingColumn = startingColumn + gridParams.w;
              return (
                <div
                  key={`${containerId}-${index}`}
                  id={containerId}
                  style={{
                    gridRowStart: startingRow,
                    gridRowEnd: endingRow,
                    gridColumnStart: startingColumn,
                    gridColumnEnd: endingColumn,
                    overflow: "hidden",
                    minHeight: gridScaling ? undefined : "180px",
                    zIndex: containers[containerId].length === 0 ? 10 : 1,
                  }}
                  data-container-wrapper="true"
                >
                  <ProfiledSortableContainer
                    key={containerId}
                    id={containerId}
                    items={containers[containerId]}
                    className="h-full rounded-lg bg-slate-800 shadow"
                    container={shopContainers.find((c) => c.id === containerId)}
                    data-scrollable-container="true"
                  >
                    {containers[containerId].length === 0 &&
                      SHOW_EMPTY_CONTAINER_PLACEHOLDER && (
                        <div className="text-center text-sm text-gray-400">
                          Drop cards here
                        </div>
                      )}

                    {containers[containerId]
                      .filter((c) => visibleCardIds.includes(c as string))
                      .map((itemId) => (
                        <SortableItem
                          key={itemId}
                          id={itemId}
                          containerId={containerId}
                          handle={true}
                          disabled={isViewOnly}
                        >
                          {({
                            dragHandleListeners,
                            dragHandleAttributes,
                            isDragging,
                          }) => (
                            <MemoizedCard
                              cardId={itemId}
                              dragHandleAttributes={dragHandleAttributes}
                              dragHandleListeners={dragHandleListeners}
                              tentative={isDragging}
                              onMoveToClick={openMoveDialog}
                            />
                          )}
                        </SortableItem>
                      ))}
                  </ProfiledSortableContainer>
                </div>
              );
            } else return null;
          })}
        </DragDropContext>
      </div>
      <InboxPanel />
      {selectedCard && (
        <MoveToDialog
          isOpen={isMoveDialogOpen}
          onClose={closeMoveDialog}
          card={selectedCard}
          onMoveSuccess={handleMoveSuccess}
        />
      )}
      <LabelChoicePopup />
      <TechnicianChoicePopup />
    </>
  );
};
